import _ from "lodash";
import normalize from "json-api-normalizer";
import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { useMedia } from "react-media";
import { useFieldArray, useFormContext } from "react-hook-form";
import { Progress, Segment, Header, Grid, Label, Divider, Placeholder } from "semantic-ui-react";
import { PermitsFormSection } from "./FormSection";
import queryString from "query-string";
import identitySelectors from "../../../selectors/identity";
import { getUser } from "../../../actions/identity";
import { getContact } from "../../../queries/contacts";
import { PermitsApplicantFormSection } from "./ApplicantFormSection";
import camelcaseKeysDeep from "camelcase-keys-deep";

import "./Form.scss";
import StaffOnlyLabel from "./StaffOnlyLabel";
import { useDebounce } from "../../../hooks/useDebounce";
import usePermit from "../hooks/permits/usePermit";
import usePermitType from "../hooks/permits/usePermitType";
import usePermitForm from "../hooks/permits/usePermitForm";
import usePermitCustomFieldsList from "../hooks/permits/usePermitCustomFieldsList";
import useNetworkHeaders from "../../../hooks/useNetworkHeaders";

interface IPermitsForm {
  permitId?: string;
  permitsTypeId: string;
  computer?: number;
  tablet?: number;
  mobile?: number;
  guided?: boolean;
  readonly?: boolean;
  editable?: boolean;
  stacked?: boolean;
  isRenew?: boolean;
  enforceRequired?: boolean;
  sectionIndex?: number;
  type?: string;
  permits_extensions_attributes?: any;
  feeDifferences?: any;
  feeDifferencesState?: any;
  renewalData?: any;
  isApplyingScreen?: boolean;
  onBack?: () => void;
  onNext?: () => void;
  onComplete?: () => void;
  addOriginalField?: React.Dispatch<any>;
  setChangedFee?: React.Dispatch<any>;
  setPermitsExtensionsAttributes?: React.Dispatch<any>;
  toggleCancel?: any;
  setPrint?: any;
  isPublicView?: boolean;
  isPrintView?: boolean;
  setPrintableFormFields?: any;
  fieldTypesToForceReadonly?: number[];
  isStaffOnly?: boolean;
}

interface FeeItem {
  attributes: {
    "fee-id": number;
    "fee-type": "dynamic" | string;
    "conditional-field-id": number;
    "conditional-amount": {
      conditions?: Record<
        number,
        {
          value: unknown;
          field_id: number;
          operator: string;
        }
      >;
    };
  };
  id: string;
  type: "fee-item";
}

export const PermitsForm = (props: IPermitsForm) => {
  const {
    guided,
    sectionIndex,
    permitId,
    permitsTypeId,
    onBack,
    onNext,
    onComplete,
    readonly,
    stacked,
    enforceRequired,
    mobile,
    type = "details",
    addOriginalField,
    setChangedFee,
    permits_extensions_attributes,
    setPermitsExtensionsAttributes,
    feeDifferences,
    isRenew,
    isStaffOnly,
    renewalData,
    isApplyingScreen = false,
    toggleCancel,
    isPublicView = false,
    setPrint,
    isPrintView,
    setPrintableFormFields,
    fieldTypesToForceReadonly = [],
  } = props;
  const { setValue, control, getValues, trigger } = useFormContext();
  const { fields, append } = useFieldArray({
    control,
    keyName: "key" as "id",
    name: "permits_extensions_attributes",
  });
  // const [readOnly, setReadOnly] = useState(readonly);
  const debouncedPermitsExtensionsAttributes = useDebounce(permits_extensions_attributes, 1000);
  const [filteredSortedFormSections, setFilteredSortedFormSections] = useState<any[]>([]);
  const networkHeaders = useNetworkHeaders();
  const permitQuery = usePermit({ permitId: Number(permitId) });
  const permitsTypeQuery = usePermitType({ permitTypeId: permitsTypeId });
  const permitsFormId = useMemo(
    () => _.get(permitsTypeQuery.data, "data.attributes.permits-form-id"),
    [permitsTypeQuery.data],
  );
  const feeItems = useMemo<{ data: FeeItem[] }>(
    () => _.get(permitsTypeQuery.data, "data.attributes.fee-items", undefined),
    [permitsTypeQuery.data],
  );
  const permitsFormQuery = usePermitForm(permitsFormId);
  const permitsTypeName = _.get(permitsTypeQuery.data, "data.attributes.name", "Loading...");

  const permitsFormFields = useMemo(
    () => normalize(permitsFormQuery.data || {})?.permitsFormField,
    [permitsFormQuery.data],
  );

  const permitsExtensions = useMemo(
    () => _.get(permitQuery.data, "data.attributes.permits-extensions", []),
    [permitQuery.data],
  );

  const permitsFormSections = useMemo(
    () => normalize(permitsFormQuery.data || {})?.permitsFormSection,
    [permitsFormQuery.data],
  );
  const permitsCustomFieldsQuery = usePermitCustomFieldsList();
  const permitsCustomFields = useMemo(
    () => normalize(permitsCustomFieldsQuery.data || {})?.customField,
    [permitsCustomFieldsQuery.data],
  );

  //get user id from the querystring
  const queryStringParams = queryString.parse(window.location.search);
  let userId = queryStringParams.userId;
  let isPublicUser = true;

  if (!userId) {
    //user id not in querystring
    //Public user, get logged in user information from identity service
    userId = identitySelectors.getUserId()?.toString() ?? "";
  } else {
    //user id exists.
    //Permit user, get permit user information from citizen service
    isPublicUser = false;
  }

  const isCitizen = identitySelectors.isCitizen();

  // query for either a public or permit user
  const personQuery = isPublicUser
    ? useQuery(["user"], getUser(networkHeaders), { retry: 3 })
    : useQuery(["citizen", userId, false], getContact(networkHeaders), { retry: 3 });

  //check if queries are still loading
  const isLoading =
    permitsTypeQuery.isLoading ||
    permitsFormQuery.isLoading ||
    permitsCustomFieldsQuery.isLoading ||
    permitQuery.isLoading ||
    personQuery.isLoading;

  const isSmallScreen = useMedia({ query: "(max-width: 767px)" });
  const sortedFormSections = useMemo(() => {
    const fieldsBySection = _.reduce(
      permitsFormFields,
      (accum: any, permitsFormField: any) => {
        const sectionId = (permitsFormField.attributes.permitsFormSectionId || Number.MAX_VALUE).toString();
        return Object.assign({}, accum, { [sectionId]: (accum[sectionId] || []).concat(permitsFormField) });
      },
      {} as any,
    );
    return _.values(Object.assign({}, permitsFormSections))
      .sort((sectionA: any, sectionB: any) => {
        return sectionA.attributes.order - sectionB.attributes.order;
      })
      .map((section: any) => Object.assign({}, section, { fields: fieldsBySection[section.id] }))
      .filter((section: any) => {
        return (
          !_.isEmpty(section.fields) &&
          (!isCitizen ||
            !section.fields.every((field: any) => {
              return _.get(field, "attributes.applicantView", "optional") === "hidden";
            }))
        );
      });
  }, [permitsFormSections, permitsFormFields, permitsTypeId]);

  const getFeeDifferences = async (data: any) => {
    if (data?.length > 0) {
      const response = await feeDifferences({ permits_extensions_attributes: data });
      if (response?.ok) {
        const json = await response.json();
        if (setChangedFee) {
          setChangedFee(json);
        }
        const temp = filteredSortedFormSections.map((section: any) => ({
          ...section,
          fields: section.fields.map((field: any) => {
            const filteredField = json.field_differences.filter(
              (item: any) => item.permits_extension_def_id === field.attributes.permitsExtensionDefId,
            );
            if (filteredField.length === 0) {
              const tempField = { ...field };
              if (tempField.attributes.amount_difference) {
                delete tempField.attributes.amount_difference;
                delete tempField.attributes.permits_extension_id;
              }
              return {
                ...tempField,
              };
            } else {
              return {
                ...field,
                attributes: {
                  ...field.attributes,
                  amount_difference: filteredField[0].amount_difference,
                  permits_extension_id: filteredField[0].permits_extension_id,
                },
              };
            }
          }),
        }));
        setFilteredSortedFormSections(temp);
      }
    }
  };

  const onChangeInnerValue = () => {
    const visibleFields: any[] = [];
    filteredSortedFormSections.forEach((section: any) => {
      section.fields.forEach((field: any) => {
        visibleFields.push(field.attributes.permitsExtensionDefId);
      });
    });
    let data = getValues().permits_extensions_attributes.map((item: any) => ({
      "id": item.id,
      "permits-extension-def-id": item.permits_extension_def_id,
      "value": item.value,
    }));
    data = data.filter((item: any) => visibleFields.includes(item["permits-extension-def-id"]));
    if (setPermitsExtensionsAttributes) {
      setPermitsExtensionsAttributes(data);
    }
  };

  // Set the fields to the current value of the permit
  useEffect(() => {
    if (stacked) {
      return;
    }
    if (permitQuery.isLoading) {
      return;
    }
    _.each(_.get(permitQuery.data, "data.attributes.permits-extensions", []), async (data: any) => {
      const value = _.get(data, "data");
      const index = _.findIndex(
        fields,
        (field: any) => value.attributes["permits-form-field-id"]?.toString() === field.permits_form_field_id,
      );
      if (index > -1) {
        setValue(`permits_extensions_attributes.${index}.value`, value.attributes.value);
        setValue(`permits_extensions_attributes.${index}.id`, value.id);
      }
    });
    if (!readonly) {
      trigger();
    }
  }, [permitQuery.data, setValue, permitsFormFields, fields, stacked, readonly, toggleCancel]);

  // Handle guided form completion
  useEffect(() => {
    if (!guided) {
      return;
    }
    if (filteredSortedFormSections.length > 0 && (sectionIndex ?? 0) + 1 == filteredSortedFormSections.length) {
      onComplete && onComplete();
    }
  }, [onComplete, sectionIndex, filteredSortedFormSections]);

  useEffect(() => {
    if (feeItems === undefined) {
      return;
    }
    const temp: any = [];
    sortedFormSections.forEach((ele, index) => {
      if (index == 0) {
        temp.push(ele);
      } else {
        if (ele.attributes.visible == true) {
          temp.push(ele);
        }
      }
    });

    temp.forEach((ele: any) => {
      ele.fields = ele.fields.filter((item: any) => item.attributes.visible);
      ele.fields = ele.fields.map((field: any) => {
        const isConditionalField = isExtensionDefConditionalField(
          field.attributes.permitsExtensionDefId,
          feeItems.data,
        );
        const isControllingField = isExtensionDefControllingField(
          field.attributes.permitsExtensionDefId,
          feeItems.data,
        );
        return {
          ...field,
          attributes: {
            ...field.attributes,
            isFeeExist: isControllingField || isConditionalField,
            isControllingField: isControllingField,
            isConditionalField: isConditionalField,
          },
        };
      });
    });
    setFilteredSortedFormSections(temp);
  }, [sortedFormSections, feeItems]);

  /**
   * Checks if the given permits form field is a controlling field for one of the permit types fee items
   *
   * @param permitsExtensionDefId the id of the field (permits extension definition)
   * @param feeItems the permit type's fee items
   * @returns
   */
  const isExtensionDefControllingField = (permitsExtensionDefId: number | string, feeItems: FeeItem[]): boolean => {
    return !!feeItems.find((item) => item.attributes["conditional-field-id"] == permitsExtensionDefId);
  };

  /**
   * Checks if the given permits form field is being used as a conditional field on one of the
   * permit type's form fields.
   *
   * @param permitsExtensionDefId the id of the permit form fields extension definition
   * @param feeItems the permit types's fee items to check against the extension def against
   *
   * @returns true, if the form field is associated with a field used for a condition on a fee item for this permit type; false otherwise
   */
  const isExtensionDefConditionalField = (permitsExtensionDefId: number | string, feeItems: FeeItem[]): boolean => {
    const conditionalFieldIds = getFeeItemConditionalFieldIds(feeItems);
    const conditionalFieldId = conditionalFieldIds.find((conditionalFieldId) => {
      // Double equals is intentional here
      return conditionalFieldId == permitsExtensionDefId;
    });
    return conditionalFieldId !== undefined;
  };

  /**
   * Gets the permits extension def ids of the conditional fields from the fee items associated with the permit's permit type
   *
   * @param feeItems the permit type's fee items
   * @returns the permits-extension-def-ids of the conditional fields
   */
  const getFeeItemConditionalFieldIds = (feeItems: FeeItem[]): number[] => {
    if (!feeItems) return [];

    return feeItems
      .map((feeItem) => {
        if (!feeItem || !feeItem.attributes) {
          return [];
        }
        const conditions = Object.values(feeItem?.attributes["conditional-amount"]?.conditions ?? {});
        return conditions.map((condition) => condition?.field_id ?? null).filter((field_id) => field_id);
      })
      .flat();
  };

  useEffect(() => {
    if (!readonly) {
      getFeeDifferences(permits_extensions_attributes);
    }
  }, [debouncedPermitsExtensionsAttributes, readonly]);

  // Change state on the print router component, so it prints once this component has rendered
  useEffect(() => {
    if (isLoading) return;
    if (isPrintView) {
      setPrint();
    }
  }, [isLoading]);

  // Generate fields for each form field
  useEffect(() => {
    _.each(_.values(permitsFormFields), (field: any) =>
      append({
        permits_form_field_id: field.id,
        permits_extension_def_id: field.attributes.permitsExtensionDefId,
        value: "",
        visible: field.attributes.visible,
        primary: field.attributes.primary,
      }),
    );
  }, [append, permitsFormFields]);

  // finds applicant from the list of contacts
  const applicant = useMemo(() => {
    if (!permitQuery.isSuccess) return;
    // find applicant from the list of contacts
    let applicantInfo = _.get(permitQuery.data, "data.attributes.permits-contacts", []).find(
      (contact: any) => contact.is_applicant,
    );

    applicantInfo = camelcaseKeysDeep(applicantInfo); // convert to camelcase
    if (!applicantInfo) return;

    // convert permitsExtensionDefId to number
    if (applicantInfo?.permitsExtensionDefId && applicantInfo.permitsExtensionDefId != null) {
      applicantInfo.permitsExtensionDefId = Number(applicantInfo.permitsExtensionDefId);
    }
    return applicantInfo;
  }, [permitQuery.data]);

  // Need to create new applicant data from the applicant to include only relevant fields for the form
  const applicantData = {
    data: {
      id: String(applicant?.userId) ?? "",
      type: applicant?.contactType,
      attributes: {
        "first-name": applicant?.firstName ?? "",
        "last-name": applicant?.lastName ?? "",
        "email": applicant?.email ?? "",
        "phone": applicant?.phone ?? "",
        "address": applicant?.address ?? "",
        "address2": applicant?.address2 ?? "",
        "country-id": applicant?.countryId,
        "city": applicant?.city ?? "",
        "state": applicant?.state ?? "",
        "zip": applicant?.zip ?? "",
        "home-address": applicant?.physicalAddress ?? "",
        "home-address2": applicant?.physicalAddress2 ?? "",
        "home-city": applicant?.physicalCity ?? "",
        "home-state": applicant?.physicalState ?? "",
        "home-zip": applicant?.physicalZip ?? "",
        "same-mail-and-physical-address": String(applicant?.sameMailAndPhysicalAddress) ?? "false",
        "company-name": applicant?.companyName ?? "",
      },
    },
  };

  const [applicantOwnerRadio, setApplicantOwnerRadio] = useState(null);

  if (isLoading) {
    return (
      <>
        {guided && (
          <Placeholder fluid className='tw-mb-12'>
            <Placeholder.Header>
              <Placeholder.Line length='full' />
            </Placeholder.Header>
          </Placeholder>
        )}
        <Segment className='tw-m-auto' basic={!guided} style={{ maxWidth: guided ? "80%" : "" }}>
          <Placeholder>
            <Placeholder.Header>
              <Placeholder.Line />
            </Placeholder.Header>
            <Placeholder.Paragraph>
              <Placeholder.Line />
              <Placeholder.Line />
              <Placeholder.Line />
              <Placeholder.Line />
            </Placeholder.Paragraph>
          </Placeholder>
        </Segment>
      </>
    );
  }

  return (
    <Segment basic className='PermitsForm'>
      {guided && (
        <Segment
          basic
          style={{ marginTop: 0, paddingTop: 0, paddingBottom: isSmallScreen ? 16 : 40, marginBottom: 16 }}
        >
          <Progress
            size='tiny'
            color='blue'
            percent={(((sectionIndex ?? 0) + 1) / filteredSortedFormSections.length) * 100.0}
          >
            <div style={{ display: "flex", gap: "2rem" }} className='tw-text-lg'>
              <span className='progress-numbers'>{`${(sectionIndex ?? 0) + 1} of ${
                filteredSortedFormSections.length
              }`}</span>
              <Header as='h1' className='permits-name tw-text-lg'>
                {permitsTypeName}
              </Header>
            </div>
            {isStaffOnly && (
              <StaffOnlyLabel
                labelStyle={{ marginTop: 4 }}
                popupPosition={"bottom right"}
                popupContent='This permit is only visible to staff'
              />
            )}
            {isRenew && (
              <Label style={{ marginTop: 4 }} circular className='renewal'>
                Renewal
              </Label>
            )}
          </Progress>
        </Segment>
      )}
      <Grid centered={stacked}>
        <Grid.Row>
          <Grid.Column computer={stacked ? 12 : 16} tablet={stacked ? 12 : 16} mobile={(mobile || 16) as any}>
            <Segment basic={!stacked} style={{ padding: stacked && !isSmallScreen ? 40 : stacked ? 16 : 0 }}>
              {filteredSortedFormSections.map((section: any, index: number) => {
                const sectionProps = {
                  key: index,
                  section: section,
                  permitsExtensions: permitsExtensions,
                  permitsFormFields: permitsFormFields,
                  enforceRequired: enforceRequired,
                  permitsCustomFields: permitsCustomFields,
                  onBack: onBack,
                  onNext: onNext,
                  addOriginalField: addOriginalField,
                  hasMore: guided && (sectionIndex || 0) < filteredSortedFormSections.length - 1,
                  lastSection: index == filteredSortedFormSections.length - 1,
                  readonly: readonly,
                  stacked: stacked,
                  type: type,
                  onChangeInnerValue: onChangeInnerValue,
                  renewalData: renewalData,
                  applicant: personQuery.isSuccess && !isRenew ? personQuery?.data : applicantData, //both identity and citizen services have same attribute payload
                  isApplyingScreen: isApplyingScreen,
                  applicantInfo: permitQuery.isSuccess ? applicant : null,
                  isPublicView: isPublicView,
                  applicantExtensionDefId: 0,
                  isPrintView: isPrintView,
                  fieldTypesToForceReadonly: fieldTypesToForceReadonly,
                };
                const visible = !guided || index === sectionIndex;
                if (index === sectionIndex || !stacked) {
                  // Applicant form section is a special case that has its own component
                  if (section.attributes.isApplicantSection)
                    return (
                      <div key={index}>
                        <PermitsApplicantFormSection
                          {...sectionProps}
                          applicantOwnerRadio={applicantOwnerRadio}
                          setApplicantOwnerRadio={setApplicantOwnerRadio}
                        />
                        {!isApplyingScreen && <Divider className='spacious' />}
                      </div>
                    );
                  // Otherwise, render the normal form section
                  return (
                    <div key={index} style={{ display: visible ? "block" : "none" }}>
                      <PermitsFormSection setPrintableFormFields={setPrintableFormFields} {...sectionProps} />
                    </div>
                  );
                }
              })}
            </Segment>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </Segment>
  );
};

export default PermitsForm;
